import React, { useState } from 'react';
import menu from '../data/menu.json';
import clsx from 'clsx';
import { Text } from '../components/base/Text';
import { useLocation } from 'react-router-dom';
import { MenuMobile } from './MenuMobile';
import { Dropdown } from '../components/base/Dropdown';
import uiConfig from '../data/uiConfig'
import ShoppingButton from '../components/base/ShoppingButton';
import { useUser } from 'context/user-context';
import { UserIcon } from '@heroicons/react/24/outline';

interface NavBarProps {
	siteName?: string;
	shadow?: boolean;
	fixed?: boolean;
	menuCentered?: boolean;
	btnContact?: boolean;
	menuMobilePosition?: string; //navbar ou bottom-right
}

export const NavBar: React.FC<NavBarProps> = ({
	siteName,
	shadow,
	fixed,
	menuCentered,
	btnContact,
	menuMobilePosition
}) => {
	const [openDropdown, setOpenDropdown] = useState<number | null>(null);
	const location = useLocation();

	const txtColor = uiConfig.navbar.color;
	const bgColor = uiConfig.navbar.bgColor;
	const hoverColor = uiConfig.navbar.hover;
	const activeClass = uiConfig.navbar.active;

	const { user, isLoading } = useUser();
	const connected = !!user;
	const isAdmin = !!user && user.role === 'admin';
	
	if (isLoading) {
		return null;
	}

	return (
		<>
			<nav
				// Si on choisit fixed => le menu reste toujours en haut
				// Si on choisit bottom-right => le menu depend de fixed
				// Si on choisit navbar => le menu reste toujours en haut en mobile et en pc il dépend de fixed
				className={clsx(
					'flex items-center px-6 py-2 flex-wrap',
					bgColor,
					shadow && 'shadow-lg',
					fixed ? 'fixed top-0 left-0 w-full z-40' : '',
					!fixed && menuMobilePosition === 'navbar' ? 'fixed md:relative top-0 left-0 w-full z-40' : '',
					connected && menuMobilePosition === 'navbar' ? 'justify-between' : 'justify-center'
				)}
				aria-label="Navigation principale"
			>
				{/* Logo à gauche */}
				<div className="flex items-center flex-shrink-0 justify-center">
					<a href="/" className="flex items-center" aria-label='Accueil'>
						<img src="/logo.svg" alt="Logo du site" className="h-14 w-auto" />
						{siteName &&
							<Text
								bold
								size='xlarge'
								className={`${txtColor} ${hoverColor} ml-3`}
							>
								{siteName}
							</Text>
						}
					</a>
				</div>

				{/* Menu centré ou à droite, caché sur mobile */}
				{connected &&
					<>
						<div
							className={clsx(
								"gap-x-8 hidden md:flex",
								menuCentered ? "flex-1 flex justify-center" : "flex-1 flex justify-end",
							)}
							aria-label="Menu principal"
						>
							{Object.values(menu).map((item: any, idx) => (
								((item.isAdmin && isAdmin) || item.isAdmin === undefined) && (
									<div
										key={idx}
										className="relative"
										onMouseEnter={() => setOpenDropdown(idx)}
									>
										<a href={item.path}>
											<Text
												className={clsx("font-title",
													txtColor,
													hoverColor,
													(location.pathname === item.path ||
														(item.path === "/backoffice" && location.pathname.includes(item.path)) ||
														item.subMenu?.some((child: any) => child.path === location.pathname)) && activeClass
												)}
											>
												{item.name}
											</Text>
										</a>
										{/* Dropdown si enfants */}
										{item.subMenu && item.subMenu.length > 0 && openDropdown === idx && (
											<Dropdown
												bgColor={bgColor}
												onMouseLeave={() => setOpenDropdown(null)}
												ariaLabel={`Sous-menu de ${item.name}`}
												className=''
											>
												{item.subMenu.map((child: any, cidx: number) => (
													<a key={cidx} href={child.path} className="block px-4 py-2">
														<Text
															className={clsx(
																txtColor,
																hoverColor,
																location.pathname === child.path && activeClass
															)}
														>
															{child.name}
														</Text>
													</a>
												))}
											</Dropdown>
										)}
									</div>

								)))}
							{connected && (
								<div className={clsx('flex items-center gap-2 cursor-pointer', txtColor, hoverColor)}>
									<a href="/utilisateur">
										<UserIcon className='w-8 h-8' />
									</a>
								</div>
							)}
							<ShoppingButton />
						</div>

						{menuMobilePosition === 'navbar' &&
							<MenuMobile btnContact={btnContact} />
						}
					</>
				}

			</nav>
		</>
	);
};